import { useEffect, useMemo, useRef } from 'react';
import Quill, { Delta } from 'quill';
import 'quill/dist/quill.snow.css';
import { v4 as uuid } from 'uuid';
import MagicUrl from 'quill-magic-url';
import ResizeModule from '@ssumo/quill-resize-module';
import 'quill-paste-smart';
import { editorFormats, toolbarPresets } from './editorConfig';
import { registerInlineStyleFormats } from './registerFormats';
import { normalizeLegacyQuillHtml } from './normalizeLegacyHtml';
import { sanitizeForSeed } from './seedSanitize';
import {
	ALLOWED_IMAGE_ACCEPT,
	ALLOWED_IMAGE_TYPES,
	MAX_IMAGE_BYTES,
	dataUrlToFile,
	isAllowedImage,
} from './imageUtils';
import { describeUploadError } from './uploadErrors';
import { EditorPreset, FormulaRequest } from './types';

const IMAGE_TYPE_MESSAGE = 'Only PNG, JPEG, GIF or WebP images can be added.';
const IMAGE_SIZE_MESSAGE = 'Image is too large (maximum 5MB).';
const PASTED_IMAGE_RETRY_SUFFIX =
	'The image stays in the editor and retries as you edit, but it will not be saved until the upload succeeds.';

/** Logs the raw error for DevTools and returns the user-facing message. */
function uploadFailureMessage(error: unknown): string {
	// eslint-disable-next-line no-console
	console.error('[rich-text-editor] image upload failed', error);
	return describeUploadError(error);
}

type UseQuillEditorArgs = {
	value?: string;
	onChange?: (value: string) => void;
	container?: string;
	disabled?: boolean;
	preset?: EditorPreset;
	uploadImage: (file: File) => Promise<string>;
	onError?: (message: string) => void;
	/** Opens the visual equation editor (insert or click-to-edit). */
	onFormulaRequest?: (request: FormulaRequest) => void;
};

let modulesRegistered = false;
function registerEditorModules() {
	if (modulesRegistered) return;
	Quill.register('modules/magicUrl', MagicUrl, true);
	Quill.register('modules/resize', ResizeModule, true);
	registerInlineStyleFormats();
	modulesRegistered = true;
}

export const useQuillEditor = ({
	value,
	onChange,
	container,
	disabled,
	preset,
	uploadImage,
	onError,
	onFormulaRequest,
}: UseQuillEditorArgs) => {
	const editor = useRef<Quill>();
	const isMounted = useRef(false);
	const seeding = useRef(false);
	const lastEmittedHtml = useRef<string>(value || '');
	const id = useRef(uuid());
	const sweepTimer = useRef<number>();
	const inFlight = useRef(new WeakSet<Element>());

	// Latest callbacks without re-initializing Quill.
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const uploadImageRef = useRef(uploadImage);
	uploadImageRef.current = uploadImage;
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;
	const onFormulaRequestRef = useRef(onFormulaRequest);
	onFormulaRequestRef.current = onFormulaRequest;

	const modules = useMemo(() => {
		return {
			toolbar: toolbarPresets[preset || 'full'],
			magicUrl: true,
			resize: {
				locale: {
					altTip: 'Resize Image',
					inputTip: 'input',
					floatLeft: 'Left',
					floatRight: 'Right',
					center: 'Center',
					restore: 'Restore',
				},
			},
			clipboard: {
				// Raw image paste (screenshot on the clipboard, no HTML).
				handleImagePaste(image: File) {
					const quill = editor.current;
					if (!quill) return;
					const range = quill.getSelection(true);
					void uploadAndInsertAt(image, range ? range.index : quill.getLength());
				},
			},
			// Replaces Quill's default drag-drop handler, which embeds
			// dropped images as base64 data: URIs instead of uploading.
			uploader: {
				mimetypes: ALLOWED_IMAGE_TYPES,
				handler(
					range: { index: number; length: number },
					files: File[]
				) {
					void handleDroppedFiles(range, files);
				},
			},
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Serializes the document for the form. Images still carrying data:
	 * URIs (pasted, upload in flight or failed) are excluded from the
	 * emitted HTML so base64 can never be persisted, even by a save that
	 * races the re-hosting sweep; once the upload lands the src swap
	 * triggers another emit that includes the hosted image.
	 */
	const serializeForEmit = (quill: Quill): string => {
		const root = quill.root;
		if (!root.querySelector('img[src^="data:"]')) return root.innerHTML;
		const clone = root.cloneNode(true) as HTMLElement;
		clone.querySelectorAll('img[src^="data:"]').forEach((img) => img.remove());
		return clone.innerHTML;
	};

	const emitChange = () => {
		const quill = editor.current;
		if (!quill) return;
		const count = quill.getContents().length() - 1;
		const html = count === 0 ? '' : serializeForEmit(quill);
		lastEmittedHtml.current = html;
		onChangeRef.current?.(html);
	};

	/**
	 * Replaces the editor content without emitting onChange or polluting
	 * undo history. Used for the initial seed and for external value
	 * changes (e.g. react-hook-form reset()).
	 */
	const seedContent = (html: string) => {
		const quill = editor.current;
		if (!quill) return;
		seeding.current = true;
		// Sanitize before assignment: innerHTML executes <img onerror=...>
		// style payloads immediately, in the staff member's session.
		quill.root.innerHTML = sanitizeForSeed(normalizeLegacyQuillHtml(html));
		quill.update();
		quill.history.clear();
		seeding.current = false;
		lastEmittedHtml.current = html;
	};

	useEffect(() => {
		if (isMounted.current) return;

		const element = document.getElementById(
			container || `${id.current}-editor`
		) as HTMLDivElement | null;
		if (!element) return;

		registerEditorModules();

		const quill = new Quill(element, {
			modules,
			formats: editorFormats,
			theme: 'snow',
			readOnly: disabled,
		});
		editor.current = quill;

		quill.on('text-change', () => {
			if (seeding.current) return;
			emitChange();
			// Safety net for base64 images that arrive inside pasted HTML
			// (Word / web pages) — quill-paste-smart admits data: URIs there.
			window.clearTimeout(sweepTimer.current);
			sweepTimer.current = window.setTimeout(sweepBase64Images, 100);
		});

		(quill.getModule('toolbar') as any)?.addHandler('image', () => {
			selectLocalImage();
		});

		// Visual equation editor instead of Quill's raw-LaTeX tooltip.
		(quill.getModule('toolbar') as any)?.addHandler('formula', () => {
			const range = quill.getSelection(true);
			onFormulaRequestRef.current?.({
				index: range ? range.index : quill.getLength(),
				length: 0,
				latex: '',
			});
		});

		// Word-style direction shortcuts: tapping the RIGHT-side Ctrl+Shift
		// pair switches the paragraph to RTL (right-aligned), the LEFT-side
		// pair back to LTR — exactly like Word/Windows. Applied on keyup of
		// a bare Ctrl+Shift tap so combos that continue with another key
		// (e.g. Ctrl+Shift+V paste) are not affected. KeyboardEvent.location
		// distinguishes the sides (1 = left, 2 = right).
		let pendingDirection: 'ltr' | 'rtl' | null = null;
		quill.root.addEventListener('keydown', (event) => {
			if (event.key === 'Control' || event.key === 'Shift') {
				if (event.ctrlKey && event.shiftKey) {
					pendingDirection = event.location === 2 ? 'rtl' : 'ltr';
				}
			} else {
				pendingDirection = null;
			}
		});
		quill.root.addEventListener('keyup', (event) => {
			if (event.key !== 'Control' && event.key !== 'Shift') return;
			const direction = pendingDirection;
			pendingDirection = null;
			if (!direction || !quill.isEnabled()) return;
			// Mirror the toolbar RTL button: alignment follows direction
			// unless the author set an explicit alignment.
			const formats = quill.getFormat();
			if (direction === 'rtl') {
				if (formats.align == null) quill.format('align', 'right', 'user');
				quill.format('direction', 'rtl', 'user');
			} else {
				if (formats.align === 'right') quill.format('align', false, 'user');
				quill.format('direction', false, 'user');
			}
		});

		// Click an existing formula to reopen it in the equation editor.
		quill.root.addEventListener('click', (event) => {
			if (!quill.isEnabled()) return;
			const target = (event.target as HTMLElement).closest?.('.ql-formula');
			if (!target || !quill.root.contains(target)) return;
			const blot = Quill.find(target);
			if (!blot) return;
			onFormulaRequestRef.current?.({
				index: quill.getIndex(blot as any),
				length: 1,
				latex: target.getAttribute('data-value') || '',
			});
		});

		seedContent(value || '');
		isMounted.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Follow external value changes. This is what makes react-hook-form
	// reset() clear or replace the editor ("Save and add another", async
	// Edit-form loads, entity switches). Echoes of our own onChange are
	// filtered via lastEmittedHtml, so a genuine external change is applied
	// even while the editor has focus — dropping it would let stale content
	// overwrite the real entity on the next save (e.g. a hard-refresh Edit
	// page whose fetch resolves while the user is already typing).
	useEffect(() => {
		if (!isMounted.current) return;
		const quill = editor.current;
		if (!quill) return;
		const incoming = value ?? '';
		if (incoming === lastEmittedHtml.current) return;
		if (incoming === quill.root.innerHTML) return;
		seedContent(incoming);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	useEffect(() => {
		if (!isMounted.current) return;
		if (disabled) editor.current?.disable();
		else editor.current?.enable();
	}, [disabled]);

	function validateImage(file: File): boolean {
		if (!isAllowedImage(file)) {
			onErrorRef.current?.(IMAGE_TYPE_MESSAGE);
			return false;
		}
		if (file.size > MAX_IMAGE_BYTES) {
			onErrorRef.current?.(IMAGE_SIZE_MESSAGE);
			return false;
		}
		return true;
	}

	/**
	 * Uploads a file and inserts the hosted URL at the captured index.
	 * The index is captured before the upload await (losing focus during
	 * a slow upload can no longer silently discard the image) and is
	 * transformed through every edit made while the upload is in flight,
	 * so the image lands where the user expects even if they kept typing.
	 */
	async function uploadAndInsertAt(file: File, index: number): Promise<void> {
		if (!validateImage(file)) return;
		const quill = editor.current;
		if (!quill) return;
		let insertAt = index;
		const track = (delta: { transformPosition(i: number): number }) => {
			insertAt = delta.transformPosition(insertAt);
		};
		quill.on('text-change', track as any);
		try {
			const url = await uploadImageRef.current(file);
			quill.off('text-change', track as any);
			const live = editor.current;
			if (!live) return;
			live.insertEmbed(insertAt, 'image', url, 'user');
			live.setSelection(insertAt + 1, 0, 'silent');
		} catch (error) {
			onErrorRef.current?.(uploadFailureMessage(error));
		} finally {
			quill.off('text-change', track as any);
		}
	}

	async function handleDroppedFiles(
		range: { index: number; length: number },
		files: File[]
	): Promise<void> {
		const quill = editor.current;
		if (!quill) return;
		// Validate before touching the document: a drop with no usable
		// image must not delete the selection it was dropped onto.
		const valid = files.filter((file) => validateImage(file));
		if (!valid.length) return;
		const removed = range.length
			? quill.getContents(range.index, range.length)
			: null;
		if (range.length) quill.deleteText(range.index, range.length, 'user');
		let index = range.index;
		let inserted = 0;
		const track = (delta: { transformPosition(i: number): number }) => {
			index = delta.transformPosition(index);
		};
		quill.on('text-change', track as any);
		try {
			for (const file of valid) {
				try {
					const url = await uploadImageRef.current(file);
					const live = editor.current;
					if (!live) return;
					// Pause tracking around our own insert so the index is
					// not double-shifted by the embed we just added.
					quill.off('text-change', track as any);
					live.insertEmbed(index, 'image', url, 'user');
					index += 1;
					inserted += 1;
					quill.on('text-change', track as any);
				} catch (error) {
					onErrorRef.current?.(uploadFailureMessage(error));
				}
			}
		} finally {
			quill.off('text-change', track as any);
			const live = editor.current;
			if (live) {
				if (removed && !inserted) {
					// Every upload failed: put the deleted selection back.
					live.updateContents(
						new Delta().retain(index).concat(removed),
						'user'
					);
					live.setSelection(index, removed.length(), 'silent');
				} else {
					live.setSelection(index, 0, 'silent');
				}
			}
		}
	}

	function selectLocalImage() {
		const quill = editor.current;
		if (!quill) return;
		const range = quill.getSelection(true);
		const insertAt = range ? range.index : quill.getLength();

		const input = document.createElement('input');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', ALLOWED_IMAGE_ACCEPT);
		input.click();

		input.onchange = () => {
			const file = input.files?.[0];
			if (!file) return;
			void uploadAndInsertAt(file, insertAt);
		};
	}

	function removeImageElement(quill: Quill, img: Element) {
		const blot = Quill.find(img);
		if (blot) {
			quill.deleteText(quill.getIndex(blot as any), 1, 'api');
		} else {
			img.remove();
		}
	}

	/**
	 * Re-hosts any base64 data: images living in the document (typically
	 * pasted from Word or a web page) through the upload service. Images
	 * that can never upload (wrong type, too large) are removed; a failed
	 * upload keeps the image visible and marked, and retries on the next
	 * edit — data: images are excluded from emitted HTML either way (see
	 * serializeForEmit), so base64 is never persisted.
	 */
	function sweepBase64Images() {
		const quill = editor.current;
		if (!quill) return;
		quill.root
			.querySelectorAll('img[src^="data:"]')
			.forEach((node) => {
				const img = node as HTMLImageElement;
				if (inFlight.current.has(img)) return;
				const file = dataUrlToFile(img.getAttribute('src') || '');
				if (!file) {
					removeImageElement(quill, img);
					onErrorRef.current?.(IMAGE_TYPE_MESSAGE);
					return;
				}
				if (file.size > MAX_IMAGE_BYTES) {
					removeImageElement(quill, img);
					onErrorRef.current?.(IMAGE_SIZE_MESSAGE);
					return;
				}
				inFlight.current.add(img);
				img.classList.remove('ql-image-upload-failed');
				img.classList.add('ql-image-uploading');
				uploadImageRef
					.current(file)
					.then((url) => {
						img.setAttribute('src', url);
						img.classList.remove('ql-image-uploading');
					})
					.catch((error) => {
						img.classList.remove('ql-image-uploading');
						img.classList.add('ql-image-upload-failed');
						onErrorRef.current?.(
							`${uploadFailureMessage(error)} ${PASTED_IMAGE_RETRY_SUFFIX}`
						);
					})
					.finally(() => {
						inFlight.current.delete(img);
						emitChange();
					});
			});
	}

	return {
		editor,
		id: container || `${id.current}-editor`,
	};
};
